import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { User } from '@openapi';
import { AuthenticationService } from '@auth/services/authentication.service';
import { StorageService } from '@services/storage.service';
import { TitleService } from '@services/title.service';
import { ProinfraBasisPageService } from '@profi/proinfra-shared-angular';
import { IonApp, IonRouterOutlet } from '@ionic/angular/standalone';
import { ChatbotAssistanceComponent } from '@components/chatbot-assistance/chatbot-assistance.component';

import { addIcons } from 'ionicons';
import * as allIcons from 'ionicons/icons';

/**
 * Root component of the application.
 * Manages user session, tab count tracking across tabs/windows,
 * initializes page title and handles user authentication state.
 */
@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: true,
  imports: [IonApp, IonRouterOutlet, ChatbotAssistanceComponent],
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly storageService = inject(StorageService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly titleService = inject(TitleService);
  private readonly proinfraBasisPageService = inject(ProinfraBasisPageService);

  private readonly TAB_COUNT_KEY = 'tabCount';
  private readonly SESSION_ACTIVE_KEY = 'sessionActive';
  private user: User | null = this.authenticationService.userValue;

  constructor() {
    // Register all Ionicons for app-wide use
    addIcons(allIcons);
  }

  /**
   * Initializes session management, page title, user session,
   * and loads page list from the Proinfra core web backend.
   */
  ngOnInit(): void {
    this.setupSessionManagement();
    this.initializePageTitle();
    this.checkUserSession();
    this.proinfraBasisPageService.fetchPageList();
  }

  /**
   * Cleans up event listeners on component destruction.
   */
  ngOnDestroy(): void {
    window.removeEventListener('beforeunload', this.handleTabClose);
    window.removeEventListener('load', this.handleTabOpen);
    window.removeEventListener('storage', this.handleStorageEvent);
  }

  /**
   * Sets up event listeners to track tab count and session state.
   */
  private setupSessionManagement(): void {
    this.syncTabCount();

    window.addEventListener('beforeunload', this.handleTabClose.bind(this));
    window.addEventListener('load', this.handleTabOpen.bind(this));
    window.addEventListener('storage', this.handleStorageEvent.bind(this));
  }

  /**
   * Decrements tab count on tab/window close.
   * Marks session inactive if no tabs remain.
   */
  private handleTabClose(): void {
    const tabCount = this.getTabCount() - 1;

    if (tabCount <= 0) {
      localStorage.setItem(this.SESSION_ACTIVE_KEY, 'false');
    }
    this.updateTabCount(tabCount);
  }

  /**
   * Increments tab count on new tab/window load.
   * Marks session active.
   */
  private handleTabOpen(): void {
    const tabCount = this.getTabCount() + 1;
    this.updateTabCount(tabCount);
    localStorage.setItem(this.SESSION_ACTIVE_KEY, 'true');
  }

  /**
   * Listens for changes to sessionActive key to trigger logout
   * when session is marked inactive in other tabs/windows.
   */
  private handleStorageEvent(event: StorageEvent): void {
    if (event.key === this.SESSION_ACTIVE_KEY && event.newValue === 'false') {
      this.authenticationService.logout();
    }
  }

  /**
   * Ensures sessionActive is set to false if no tabs are open.
   */
  private syncTabCount(): void {
    const tabCount = this.getTabCount();
    if (tabCount === 0) {
      localStorage.setItem(this.SESSION_ACTIVE_KEY, 'false');
    }
  }

  /**
   * Updates the stored tab count; removes the key if count is zero or less.
   * @param count - current number of open tabs
   */
  private updateTabCount(count: number): void {
    if (count <= 0) {
      localStorage.removeItem(this.TAB_COUNT_KEY);
    } else {
      localStorage.setItem(this.TAB_COUNT_KEY, count.toString());
    }
  }

  /**
   * Reads the current tab count from session storage.
   * @returns number of open tabs tracked
   */
  private getTabCount(): number {
    return Number(localStorage.getItem(this.TAB_COUNT_KEY) || '0');
  }

  /**
   * Initializes the page title via TitleService.
   */
  private initializePageTitle(): void {
    this.titleService.setTitle();
  }

  /**
   * Checks user authentication status.
   * If authenticated, initializes storage service;
   * otherwise redirects to login page.
   */
  private checkUserSession(): void {
    if (this.user) {
      this.storageService.init().subscribe({
        error: error => console.error('Storage initialization failed:', error),
      });
    } else {
      this.router.navigate(['/login']);
    }
  }
}
